import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { EstadoColada } from '@adelante/shared';

/**
 * Badge con color semántico por estado de colada.
 *
 *  - sugerida    → gris      (aún en triage automático)
 *  - confirmada  → azul      (operador validó)
 *  - digitada    → verde     (entró a BC)
 *  - cerrada     → verde sólido (terminó el ciclo)
 *  - anulada     → rojo      (descartada)
 *  - anulada por consolidación → naranja "Consolidada" (estado = anulada
 *    + motivo_anulacion empieza con "Consolidada en colada #"). Lo
 *    distinguimos visualmente porque NO es un descarte — los batches
 *    siguen procesándose en la colada destino.
 */

const VARIANTES: Record<EstadoColada, string> = {
  sugerida: 'border-zinc-300 bg-zinc-100 text-zinc-700 hover:bg-zinc-100',
  confirmada: 'border-blue-300 bg-blue-100 text-blue-800 hover:bg-blue-100',
  digitada: 'border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  cerrada: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-600',
  anulada: 'border-red-300 bg-red-100 text-red-800 hover:bg-red-100',
};

const VARIANTE_CONSOLIDADA = 'border-orange-300 bg-orange-100 text-orange-900 hover:bg-orange-100';

const LABELS: Record<EstadoColada, string> = {
  sugerida: 'Sugerida',
  confirmada: 'Confirmada',
  digitada: 'Digitada',
  cerrada: 'Cerrada',
  anulada: 'Anulada',
};

/** Patrón del motivo que distingue una consolidación de una anulación manual. */
const PREFIJO_CONSOLIDACION = /^Consolidada en colada #/i;

export function esConsolidacion(
  estado: EstadoColada,
  motivoAnulacion: string | null | undefined,
): boolean {
  return estado === 'anulada' && !!motivoAnulacion && PREFIJO_CONSOLIDACION.test(motivoAnulacion);
}

interface Props {
  estado: EstadoColada;
  /**
   * Si está presente y el estado es `anulada`, lo usamos para detectar
   * consolidaciones y mostrar un badge "Consolidada" en naranja con
   * tooltip del motivo. Sin esto, las anuladas se ven todas iguales.
   */
  motivoAnulacion?: string | null;
  className?: string;
}

export function BadgeEstadoColada({ estado, motivoAnulacion, className }: Props) {
  const consolidada = esConsolidacion(estado, motivoAnulacion);
  const variante = consolidada ? VARIANTE_CONSOLIDADA : VARIANTES[estado];
  const label = consolidada ? 'Consolidada' : LABELS[estado];
  return (
    <Badge
      variant="outline"
      className={cn('font-medium', variante, className)}
      title={motivoAnulacion ?? undefined}
    >
      {label}
    </Badge>
  );
}
